import { StatusContainer } from '@servicetitan/financing-utils-shared';
import {
    useProviderConfig,
    useSecondLookProviderName,
} from '@servicetitan/financing-workflow-shared';
import ReviewingIllustration from '../../common/illustrations/reviewing.svg';
import { VerticalStepper, type StepItem } from './VerticalStepper';

export type ApplicationState = 'sending' | 'reviewing' | 'approved' | 'declined';

export type SecondLookState = 'sending' | 'reviewing' | 'approved' | 'declined' | 'none';

interface ReviewingProps {
    applicationState: ApplicationState;
    secondLookState: SecondLookState;
    /**
     * Omit first-look submit/review rows. Direct Finturf/Turns apply never ran a first-look
     * submission, so the stepper should only show the second-look provider's progress.
     */
    hideFirstLookSteps?: boolean;
}

export const Reviewing = ({
    applicationState,
    secondLookState = 'none',
    hideFirstLookSteps = false,
}: ReviewingProps) => {
    const providerConfig = useProviderConfig();
    const secondLookProviderName = useSecondLookProviderName();

    const steps: StepItem[] = [];

    if (!hideFirstLookSteps) {
        if (applicationState === 'sending') {
            steps.push({
                label: providerConfig.submittingLabel ?? 'Submitting Application',
                description: 'Your application is being submitted.',
                completed: false,
            });
        } else {
            steps.push({
                label: providerConfig.submittedLabel ?? 'Application Submitted',
                description: 'Your request has been sent.',
                completed: true,
            });
        }

        if (applicationState === 'reviewing') {
            steps.push({
                label: providerConfig.reviewingNow ?? 'Reviewing now',
                description: providerConfig.reviewingNowDescription ?? 'Checking if you qualify.',
                completed: false,
            });
        } else if (applicationState === 'approved') {
            steps.push({
                label: providerConfig.reviewingNowComplete ?? 'Review Complete',
                description: '',
                completed: true,
            });
        } else if (applicationState === 'declined') {
            steps.push({
                label: providerConfig.reviewingNowComplete ?? 'Review Complete',
                description: providerConfig.reviewingDeclined ?? 'Unable to prequalify you.',
                completed: true,
            });
        }
    }

    if (secondLookState === 'reviewing') {
        steps.push({
            label: `${secondLookProviderName} is Reviewing Now`,
            description: 'Exploring additional financing options.',
            completed: false,
        });
    } else if (secondLookState === 'approved') {
        steps.push({
            label: `${secondLookProviderName} Review Complete`,
            description: '',
            completed: true,
        });
    } else if (secondLookState === 'sending') {
        steps.push({
            label: `Submitting ${secondLookProviderName} Application`,
            description: `Sending your information to ${secondLookProviderName}.`,
            completed: false,
        });
    }

    return (
        <StatusContainer headline="Reviewing your information" image={ReviewingIllustration}>
            <VerticalStepper steps={steps} />
        </StatusContainer>
    );
};
